import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';

const EnhancedBubble = ({ data, onPop, onRemove }) => {
  const [health, setHealth] = useState(data.health || 1);
  const [isHit, setIsHit] = useState(false);
  const [timeLeft, setTimeLeft] = useState(data.duration);
  const [isExpiring, setIsExpiring] = useState(false);
  const timerRef = useRef(null);
  const isProcessing = useRef(false);
  const lastUpdateTime = useRef(Date.now());
  const isMounted = useRef(true);
  const isRemoving = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let rafId;
    const updateTimer = () => {
      if (!isMounted.current || isRemoving.current) return;

      const now = Date.now();
      const delta = now - lastUpdateTime.current;
      lastUpdateTime.current = now;

      setTimeLeft(prev => {
        const newTime = Math.max(0, prev - delta);
        
        if (newTime <= 1000 && !isExpiring) {
          setIsExpiring(true);
        }
        
        if (newTime <= 0) {
          cancelAnimationFrame(rafId);
          if (isMounted.current && !isRemoving.current) {
            isRemoving.current = true;
            onRemove(data.id, false);
          }
          return 0;
        }
        
        return newTime;
      });

      rafId = requestAnimationFrame(updateTimer);
    };

    rafId = requestAnimationFrame(updateTimer);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [data.id, onRemove, isExpiring]);

  const handleInteraction = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (isProcessing.current || !isMounted.current || isRemoving.current) return;

    isProcessing.current = true;

    setIsHit(true);
    setTimeout(() => {
      if (isMounted.current) {
        setIsHit(false);
        isProcessing.current = false;
      }
    }, 150);

    const newHealth = health - 1;
    setHealth(newHealth);

    if (newHealth <= 0) {
      isRemoving.current = true;
      onPop(data.id, data);
    }
  };

  const IconComponent = FaIcons[data.icon];
  const progress = (timeLeft / data.duration) * 100;

  const isMobile = window.innerWidth < 768;
  const healthBadgeSize = isMobile ? 'w-10 h-10' : 'w-12 h-12';
  const healthTextSize = isMobile ? 'text-base' : 'text-lg';
  const healthLabelSize = isMobile ? 'text-[7px]' : 'text-[8px]';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ 
          scale: isExpiring ? [1, 1.1, 1] : 1, 
          opacity: isExpiring ? [1, 0.7, 1] : 1 
        }}
        exit={{ scale: 0, opacity: 0 }}
        transition={isExpiring ? { duration: 0.5, repeat: Infinity } : {}}
        onClick={handleInteraction}
        onTouchStart={handleInteraction}
        data-bubble
        className={`
          absolute cursor-pointer select-none transition-all duration-200
          ${isHit ? 'scale-90' : 'hover:scale-110 active:scale-95'}
          ${data.isSpecial ? 'z-20' : 'z-10'}
          ${isExpiring ? 'animate-pulse' : ''}
        `}
        style={{
          left: data.x,
          top: data.y,
          width: data.size,
          height: data.size,
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        <div
          className={`
            w-full h-full rounded-full shadow-xl flex items-center justify-center relative overflow-hidden
            bg-white/90 backdrop-blur-sm border-4
            ${data.isSpecial ? 'animate-pulse' : ''}
          `}
          style={{
            borderColor: data.color || '#fff',
            boxShadow: data.isSpecial 
              ? `0 0 20px ${data.color}80, 0 10px 30px rgba(0,0,0,0.3)` 
              : '0 10px 30px rgba(0,0,0,0.2)'
          }}
        >
          {data.isSpecial && (
            <div 
              className="absolute inset-0 rounded-full opacity-30 animate-ping" 
              style={{ backgroundColor: data.color }}
            />
          )}

          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              fill="none"
              stroke={progress < 20 ? '#ef4444' : data.color}
              strokeWidth="3"
              strokeDasharray={`${progress * 2.83} 283`}
              opacity={progress < 20 ? "0.8" : "0.3"}
            />
          </svg>

          <div 
            className="relative z-10 transition-transform" 
            style={{
              fontSize: data.size * 0.45,
              color: data.color,
              transform: isHit ? 'scale(0.8)' : 'scale(1)'
            }}
          >
            {IconComponent && <IconComponent />}
          </div>

          {data.isSpecial && health > 0 && (
            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 z-30">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500 rounded-full blur-md opacity-50 animate-pulse" />
                <div className={`relative ${healthBadgeSize} bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full flex flex-col items-center justify-center shadow-xl border-2 sm:border-3 border-white`}>
                  <div className={`${healthTextSize} font-black leading-none`}>
                    {health}
                  </div>
                  <div className={`${healthLabelSize} font-bold leading-none opacity-90`}>
                    次
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="absolute top-2 left-2 w-1/3 h-1/3 bg-white/40 rounded-full blur-md" />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EnhancedBubble;